import { FC } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Typography,
  Input,
  Textarea,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { LoadingButton } from '@altimateai/lego'
import { getAvailableModels } from "../../services/evals";

const createAgentSchema = z.object({
  name: z.string().min(1, "Agent name is required"),
  description: z.string().min(1, "Description is required"),
  prompt: z.string().min(1, "Prompt is required"),
  model: z.string().min(1, "Model is required"),
});

export type CreateAgentFormData = z.infer<typeof createAgentSchema>;

interface CreateAgentFormProps {
  onSubmit: (data: CreateAgentFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const CreateAgentForm: FC<CreateAgentFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  // Fetch available models from backend
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ["availableModels"],
    queryFn: getAvailableModels,
  });

  const availableModels = modelsData?.models || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateAgentFormData>({
    resolver: zodResolver(createAgentSchema),
    defaultValues: {
      name: "",
      description: "",
      prompt: "",
      model: availableModels[0]?.id || "",
    },
  });

  const selectedModel = watch("model");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="al-space-y-4">
      <div className="al-space-y-2">
        <Label htmlFor="agent-name">
          <Typography variant="body" weight="medium">
            Agent Name <span className="al-text-red-500">*</span>
          </Typography>
        </Label>
        <Input
          id="agent-name"
          placeholder="e.g., sql-expert, data-analyst"
          {...register("name")}
          className={errors.name ? "al-border-red-500" : ""}
        />
        {errors.name && (
          <Typography className="al-text-red-500">
            {errors.name.message}
          </Typography>
        )}
      </div>

      <div className="al-space-y-2">
        <Label htmlFor="agent-description">
          <Typography variant="body" weight="medium">
            Description <span className="al-text-red-500">*</span>
          </Typography>
        </Label>
        <Textarea
          id="agent-description"
          placeholder="Brief description of what this agent does"
          rows={3}
          {...register("description")}
          className={errors.description ? "al-border-red-500" : ""}
        />
        {errors.description && (
          <Typography className="al-text-red-500">
            {errors.description.message}
          </Typography>
        )}
      </div>

      <div className="al-space-y-2">
        <Label htmlFor="agent-model">
          <Typography variant="body" weight="medium">
            Model to Evaluate <span className="al-text-red-500">*</span>
          </Typography>
        </Label>
        <Select
          value={selectedModel}
          onValueChange={value => setValue("model", value)}
          disabled={modelsLoading}
        >
          <SelectTrigger id="agent-model" className="al-w-full">
            <SelectValue placeholder={modelsLoading ? "Loading models..." : "Select a model"} />
          </SelectTrigger>
          <SelectContent>
            {availableModels.map(model => (
              <SelectItem key={model.id} value={model.id}>
                <Stack direction="column" gap="xs">
                  <span className="al-font-medium">{model.name}</span>
                  <span className="al-text-xs al-text-gray-500">{model.description}</span>
                </Stack>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.model && (
          <Typography className="al-text-red-500">
            {errors.model.message}
          </Typography>
        )}
      </div>

      <div className="al-space-y-2">
        <Label htmlFor="agent-prompt">
          <Typography variant="body" weight="medium">
            System Prompt <span className="al-text-red-500">*</span>
          </Typography>
        </Label>
        <Textarea
          id="agent-prompt"
          placeholder="Enter the system prompt for this agent..."
          rows={10}
          {...register("prompt")}
          className={`al-font-mono al-text-sm ${errors.prompt ? "al-border-red-500" : ""}`}
        />
        {errors.prompt && (
          <Typography className="al-text-red-500">
            {errors.prompt.message}
          </Typography>
        )}
      </div>

      <Stack direction="row" gap="sm" justify="end" className="al-pt-4">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton loading={isSubmitting} type="submit">
          Create Agent
        </LoadingButton>
      </Stack>
    </form>
  );
};
